import { useElementSize } from '@lighthouse/bui'
import type { ActorEvent, AudioBlockAbstract, IconDecorationProtocol } from '@lighthouse/core'
import { AudioVariant, EVENT_TYPE } from '@lighthouse/core'
import {
    aliyunVideoReg,
    flexMinSizeStyle,
    getImageFullUrlInApplication,
    getTextColorStyle,
    getWithScopeIdAndRecordIdAndColumnId,
    PlaybackBar,
    PlayOnlyButton,
    useActionRunningLoadings,
    useApplicationContext,
    useAppThemeContext,
    useAudioPlayer,
    VoiceButton
} from '@lighthouse/shared'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { AnimationDecorators } from '../../components'
import { useContainerBlockContext, useCustomViewBlockContext, useEffectiveAnchorId } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'

const VOICE_BASE = { width: 72, height: 24 }
const BUTTON_BASE = { width: 24, height: 24 }

interface AudioBlockProps {
    blockData: AudioBlockAbstract
    value: string
    onTriggerEvent?: (id: string, actorEvent: ActorEvent) => Promise<void>
}

const SCxContainer = styled(AnimationDecorators)`
    width: 100%;
    height: 100%;
    overflow: hidden;
`

const ScaleBox = styled.div<{ scale: number; baseWidth: number; baseHeight: number }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;

    > div {
        width: ${({ baseWidth }) => baseWidth}px;
        height: ${({ baseHeight }) => baseHeight}px;
        transform: scale(${({ scale }) => scale});
        transform-origin: center;
    }
`

const BarBox = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
`

const isPlatformAudioUrl = (url: string) => {
    if (!url) {
        return false
    }
    if (aliyunVideoReg.test(url)) {
        return true
    }
    return url.includes('cdn.data.byecode.com') || url.includes('cdn.data-test.byecode.com')
}

const DEFAULT_ICON_COLOR = 'var(--color-gray-800, #1d2939)'

const resolveStatusIcon = (
    specific: IconDecorationProtocol | undefined,
    legacy: IconDecorationProtocol | undefined
) => {
    if (specific) {
        return specific.value ? specific : undefined
    }
    return legacy?.value ? legacy : undefined
}

const AudioBlock: React.FunctionComponent<AudioBlockProps> = ({ blockData, value, onTriggerEvent }) => {
    const { config, id, title } = blockData
    const {
        variant = AudioVariant.bar,
        showDuration = true,
        isLoop = false,
        preload = false,
        icon,
        pausedIcon,
        playingIcon,
        iconColor,
        playingIconColor,
        eventList,
        breakPoint,
        anchor
    } = config
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const { handleActionTriggerWithLoading } = useActionRunningLoadings()
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id), [id, scope, record?.id])
    const playEvent = useMemo(() => eventList?.find(ev => ev.type === EVENT_TYPE.PLAY), [eventList])
    const handlePlayEvent = useCallback(() => {
        if (!playEvent) {
            return
        }
        handleActionTriggerWithLoading({ id: uniqueId, actorEvent: playEvent, trigger: onTriggerEvent })
    }, [handleActionTriggerWithLoading, onTriggerEvent, playEvent, uniqueId])

    const { fetchAVPlayUrl, appId } = useApplicationContext()
    const { theme } = useAppThemeContext()
    const { ref, width, height } = useElementSize<HTMLDivElement>()
    const [playUrl, setPlayUrl] = useState('')

    useEffect(() => {
        let cancelled = false

        const resolve = async () => {
            if (!value) {
                setPlayUrl('')
                return
            }
            if (!isPlatformAudioUrl(value) || !fetchAVPlayUrl) {
                setPlayUrl(value)
                return
            }
            try {
                const auth = await fetchAVPlayUrl(value, true)
                if (!cancelled) {
                    setPlayUrl(auth?.playUrl || value)
                }
            } catch {
                if (!cancelled) {
                    setPlayUrl(value)
                }
            }
        }

        void resolve()
        return () => {
            cancelled = true
        }
    }, [fetchAVPlayUrl, value])

    const player = useAudioPlayer({
        src: playUrl,
        isLoop,
        preload,
        showDuration: variant !== AudioVariant.button && showDuration,
        onPlay: handlePlayEvent
    })

    const showTime = variant !== AudioVariant.button && showDuration
    const resolvedIconColor = useMemo(() => {
        const defaultColor = getTextColorStyle(iconColor, theme.palettes).color || DEFAULT_ICON_COLOR
        const playingColor = getTextColorStyle(playingIconColor, theme.palettes).color || defaultColor
        return player.playing ? playingColor : defaultColor
    }, [iconColor, player.playing, playingIconColor, theme.palettes])
    const resolveIconUrl = useCallback((url: string) => getImageFullUrlInApplication(appId, url), [appId])
    const statusIcon = useMemo(
        () => resolveStatusIcon(player.playing ? playingIcon : pausedIcon, icon),
        [icon, pausedIcon, player.playing, playingIcon]
    )
    const scale = useMemo(() => {
        const base = variant === AudioVariant.voice ? VOICE_BASE : BUTTON_BASE
        const nextWidth = width || base.width
        const nextHeight = height || base.height
        return Math.min(nextWidth / base.width, nextHeight / base.height) || 1
    }, [height, variant, width])

    const skin = useMemo(() => {
        if (variant === AudioVariant.voice) {
            return (
                <ScaleBox scale={scale} baseWidth={VOICE_BASE.width} baseHeight={VOICE_BASE.height}>
                    <div>
                        <VoiceButton
                            playing={player.playing}
                            duration={player.duration}
                            showDuration={showTime}
                            error={player.error}
                            icon={statusIcon}
                            iconColor={resolvedIconColor}
                            onGetFullUrl={resolveIconUrl}
                            onToggle={player.toggle}
                        />
                    </div>
                </ScaleBox>
            )
        }
        if (variant === AudioVariant.button) {
            return (
                <ScaleBox scale={scale} baseWidth={BUTTON_BASE.width} baseHeight={BUTTON_BASE.height}>
                    <div>
                        <PlayOnlyButton
                            playing={player.playing}
                            error={player.error}
                            icon={statusIcon}
                            iconColor={resolvedIconColor}
                            onGetFullUrl={resolveIconUrl}
                            onToggle={player.toggle}
                        />
                    </div>
                </ScaleBox>
            )
        }
        return (
            <BarBox>
                <PlaybackBar
                    playing={player.playing}
                    currentTime={player.currentTime}
                    duration={player.duration}
                    showDuration={showTime}
                    error={player.error}
                    icon={statusIcon}
                    iconColor={resolvedIconColor}
                    onGetFullUrl={resolveIconUrl}
                    onToggle={player.toggle}
                    onSeek={player.seek}
                />
            </BarBox>
        )
    }, [player, resolveIconUrl, resolvedIconColor, scale, showTime, statusIcon, variant])

    return (
        <SCxContainer
            ref={ref}
            blockId={id}
            blockName={title}
            animation={breakPoint.animation}
            id={effectiveAnchorId}
            data-stop-action-propagation
            style={flexMinSizeStyle}
        >
            {skin}
        </SCxContainer>
    )
}

export default memo(AudioBlock)
